import firebase from "firebase/app";
import "firebase/functions";

import type { EBayLimit } from "src/interfaces/supportState.interface";
import type {
  ClientVersionStats,
  UserClientVersion,
} from "src/interfaces/clientVersionStats.interface";
import type { LoadTimingStats } from "src/interfaces/loadTimingStats.interface";
import type {
  SalesDatesCompareResult,
  SalesDatesRunDetail,
  SalesDatesRunsPage,
  SalesDatesUserDetail,
} from "src/interfaces/salesDatesRuns.interface";
import type {
  ClientErrorsPage,
  ErrorStats,
} from "src/interfaces/errorStats.interface";
import type { SupportUserEventsPage } from "src/interfaces/userEvents.interface";
import type { PlatformLimitsResponse } from "src/interfaces/platformLimits.interface";
import type MonthYear from "src/interfaces/monthYear.interface";
import type {
  SupportErrorAlertsPage,
  SupportErrorAlertSearchFilter,
  SupportErrorAlertTriageStatus,
} from "src/interfaces/supportErrorAlerts.interface";
import type { Cookie } from "src/utils/extension";

function supportLogin(username: string, password: string) {
  const fn = firebase.functions().httpsCallable("support-login");
  return fn({ username, password });
}

function supportGenerateToken(token: string, username: string) {
  const fn = firebase.functions().httpsCallable("support-generateToken");
  return fn({ token, username });
}

async function supportGetUser(token: string, username: string) {
  const fn = firebase.functions().httpsCallable("support-getUser");
  return await fn({ token, username });
}

export interface SupportUpdateUser {
  email?: string;
  emailVerified?: boolean;
  disabled?: boolean;
  /** Replaces the entire `featureFlags` map on the Firestore `Users` document. */
  featureFlags?: Record<string, boolean>;
}

async function supportUpdateUser(
  token: string,
  username: string,
  data: SupportUpdateUser
) {
  const fn = firebase.functions().httpsCallable("support-updateUser");
  return await fn({ ...data, username, token });
}

async function supportGetEBayLimits(
  token: string
): Promise<{ data: { rateLimits: EBayLimit } }> {
  const fn = firebase.functions().httpsCallable("support-getEBayLimits");
  return await fn({ token });
}

async function supportGetLimits(
  token: string,
  {
    platform,
    from,
    to,
  }: {
    platform: string;
    from: string;
    to?: string;
  }
): Promise<{ data: PlatformLimitsResponse }> {
  const fn = firebase.functions().httpsCallable("support-getLimits");
  return await fn({
    token,
    platform,
    from,
    ...(to != null ? { to } : {}),
  });
}

async function supportSetIntegrationSync(
  token: string,
  id: string,
  { sync, inventory }: { sync?: boolean; inventory?: boolean }
): Promise<{ data: { id: string; sync: boolean; inventory: boolean } }> {
  const fn = firebase.functions().httpsCallable("support-setIntegrationSync");
  return await fn({ token, id, sync, inventory });
}

async function supportDispatchIntegrationImport(
  token: string,
  integrationId: string,
  importJob: "direct-import-inventory"
);
async function supportDispatchIntegrationImport(
  token: string,
  integrationId: string,
  importJob: "daily-sync"
);
async function supportDispatchIntegrationImport(
  token: string,
  integrationId: string,
  importJob: "direct-import-sales",
  args: { start: MonthYear; end: MonthYear }
);
async function supportDispatchIntegrationImport(
  token: string,
  integrationId: string,
  importJob: "direct-import-inventory" | "daily-sync" | "direct-import-sales",
  args?: { start: MonthYear; end: MonthYear }
) {
  const fn = firebase
    .functions()
    .httpsCallable("support-dispatchIntegrationImport");
  return await fn({ token, integrationId, importJob, args });
}

async function supportUpdateIntegrationLastSyncDates(
  token: string,
  id: string,
  { sales, inventory }: { sales?: string; inventory?: string }
) {
  const fn = firebase
    .functions()
    .httpsCallable("support-updateIntegrationLastSyncDates");
  return await fn({ token, id, sales, inventory });
}

async function supportDeleteAccount(token: string, username: string) {
  const fn = firebase.functions().httpsCallable("support-deleteAccount");
  return await fn({ token, username });
}

export interface SupportAccessToken {
  id: string;
  integration: string;
  createTime?: unknown;
  cookies?: Record<string, string> | Cookie[];
  jwt?: string;
  userAgent?: string;
}

async function supportGetAccessToken(
  token: string,
  integrationId: string
): Promise<{ data: SupportAccessToken }> {
  const fn = firebase.functions().httpsCallable("support-getAccessToken");
  return await fn({ token, integrationId });
}

async function supportGetClientVersions(
  token: string,
  from: string,
  to: string
): Promise<{ data: ClientVersionStats }> {
  const fn = firebase.functions().httpsCallable("support-getClientVersions");
  return await fn({ token, from, to });
}

async function supportGetLoadTimingStats(
  token: string,
  from: string,
  to: string,
  featureFlag?: string
): Promise<{ data: LoadTimingStats }> {
  const fn = firebase.functions().httpsCallable("support-getLoadTimingStats");
  return await fn({ token, from, to, featureFlag: featureFlag || null });
}

async function supportGetUserClientVersion(
  token: string,
  userId: string
): Promise<{ data: { version: UserClientVersion | null } }> {
  const fn = firebase.functions().httpsCallable("support-getUserClientVersion");
  return await fn({ token, userId });
}

async function supportGetErrorStats(
  token: string,
  from: string,
  to: string
): Promise<{ data: ErrorStats }> {
  const fn = firebase.functions().httpsCallable("support-getErrorStats");
  return await fn({ token, from, to });
}

async function supportGetErrors(
  token: string,
  from: string,
  to: string,
  options?: { limit?: number; cursor?: string }
): Promise<{ data: ClientErrorsPage }> {
  const fn = firebase.functions().httpsCallable("support-getErrors");
  return await fn({
    token,
    from,
    to,
    ...(options?.limit != null ? { limit: options.limit } : {}),
    ...(options?.cursor ? { cursor: options.cursor } : {}),
  });
}

async function supportGetUserErrors(
  token: string,
  userId: string,
  options?: { limit?: number; cursor?: string }
): Promise<{ data: ClientErrorsPage }> {
  const fn = firebase.functions().httpsCallable("support-getUserErrors");
  return await fn({
    token,
    userId,
    ...(options?.limit != null ? { limit: options.limit } : {}),
    ...(options?.cursor ? { cursor: options.cursor } : {}),
  });
}

async function supportGetUserEvents(
  token: string,
  userId: string,
  from: string,
  to: string,
  options?: { limit?: number; cursor?: string }
): Promise<{ data: SupportUserEventsPage }> {
  const fn = firebase.functions().httpsCallable("support-getUserEvents");
  return await fn({
    token,
    userId,
    from,
    to,
    ...(options?.limit != null ? { limit: options.limit } : {}),
    ...(options?.cursor ? { cursor: options.cursor } : {}),
  });
}

async function supportGetSalesDatesRuns(
  token: string,
  options?: { limit?: number; cursor?: string }
): Promise<{ data: SalesDatesRunsPage }> {
  const fn = firebase.functions().httpsCallable("support-getSalesDatesRuns");
  return await fn({
    token,
    ...(options?.limit != null ? { limit: options.limit } : {}),
    ...(options?.cursor ? { cursor: options.cursor } : {}),
  });
}

async function supportGetSalesDatesRun(
  token: string,
  runId: string,
  options?: { includeUsers?: boolean }
): Promise<{ data: SalesDatesRunDetail }> {
  const fn = firebase.functions().httpsCallable("support-getSalesDatesRun");
  return await fn({
    token,
    runId,
    ...(options?.includeUsers ? { includeUsers: true } : {}),
  });
}

async function supportGetSalesDatesRunUser(
  token: string,
  runId: string,
  uid: string,
  options?: { limit?: number; offset?: number }
): Promise<{ data: SalesDatesUserDetail }> {
  const fn = firebase.functions().httpsCallable("support-getSalesDatesRunUser");
  return await fn({
    token,
    runId,
    uid,
    ...(options?.limit != null ? { limit: options.limit } : {}),
    ...(options?.offset != null ? { offset: options.offset } : {}),
  });
}

async function supportCompareSalesDatesRuns(
  token: string,
  previousRunId: string,
  currentRunId: string
): Promise<{ data: SalesDatesCompareResult }> {
  const fn = firebase
    .functions()
    .httpsCallable("support-compareSalesDatesRuns");
  return await fn({ token, previousRunId, currentRunId });
}

async function supportTriggerSalesDatesRun(
  token: string
): Promise<{ data: { runId: string } }> {
  const fn = firebase.functions().httpsCallable("support-triggerSalesDatesRun");
  return await fn({ token });
}

async function supportGetErrorAlerts(
  token: string,
  from: string,
  to: string,
  options?: {
    limit?: number;
    cursor?: string;
    filters?: SupportErrorAlertSearchFilter[];
  }
): Promise<{ data: SupportErrorAlertsPage }> {
  const fn = firebase.functions().httpsCallable("support-getErrorAlerts");
  return await fn({
    token,
    from,
    to,
    ...(options?.limit != null ? { limit: options.limit } : {}),
    ...(options?.cursor ? { cursor: options.cursor } : {}),
    ...(options?.filters?.length ? { filters: options.filters } : {}),
  });
}

async function supportUpdateErrorAlertTriage(
  token: string,
  alertId: string,
  triageStatus: SupportErrorAlertTriageStatus
): Promise<{
  data: { alertId: string; triageStatus: SupportErrorAlertTriageStatus };
}> {
  const fn = firebase
    .functions()
    .httpsCallable("support-updateErrorAlertTriage");
  return await fn({ token, alertId, triageStatus });
}

async function supportUpdateErrorAlertVisibility(
  token: string,
  alertId: string,
  isVisibleToUser: boolean
): Promise<{ data: { alertId: string; isVisibleToUser: boolean } }> {
  const fn = firebase
    .functions()
    .httpsCallable("support-updateErrorAlertVisibility");
  return await fn({ token, alertId, isVisibleToUser });
}

async function supportSendErrorAlertToSlack(
  token: string,
  alertId: string,
  note?: string
): Promise<{
  data: {
    alertId: string;
    sentToSlack: string;
    triageStatus: SupportErrorAlertTriageStatus;
    userEmail: string;
    warning: string | null;
  };
}> {
  const fn = firebase
    .functions()
    .httpsCallable("support-sendErrorAlertToSlack");
  return await fn({
    token,
    alertId,
    ...(note ? { note } : {}),
  });
}

export {
  supportLogin,
  supportGenerateToken,
  supportGetUser,
  supportUpdateUser,
  supportGetEBayLimits,
  supportGetLimits,
  supportSetIntegrationSync,
  supportDispatchIntegrationImport,
  supportUpdateIntegrationLastSyncDates,
  supportDeleteAccount,
  supportGetAccessToken,
  supportGetClientVersions,
  supportGetLoadTimingStats,
  supportGetUserClientVersion,
  supportGetErrorStats,
  supportGetErrors,
  supportGetUserErrors,
  supportGetUserEvents,
  supportGetSalesDatesRuns,
  supportGetSalesDatesRun,
  supportGetSalesDatesRunUser,
  supportCompareSalesDatesRuns,
  supportTriggerSalesDatesRun,
  supportGetErrorAlerts,
  supportUpdateErrorAlertTriage,
  supportUpdateErrorAlertVisibility,
  supportSendErrorAlertToSlack,
};
